import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import Swal from 'sweetalert2';
import { UnsubscribeOnDestroyAdapter } from '../../../_shared';
import { AuthService } from '../../../_infrastructure/services/auth/user-auth/auth.service';
import { MidiaImageBase64Dto } from '../../../_infrastructure/dtos/core/midia/MidiaImagemBase64Dto';
import { MediaImageService } from '../../../_shared/utils/MediaImage.service';
import { PerfilResponse } from '../../../_infrastructure/dtos/core/perfil/PerfilResponse';
import { PerfilService } from '../../../_infrastructure/services/core/perfil/perfil.service';
import { FormService } from '../../../_shared/utils/form.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DialogData } from '../../../_infrastructure/dtos/DialogDataDto';
import { CargoResponse } from '../../../_infrastructure/dtos/core/cargo/CargoResponse';
import { CargoService } from '../../../_infrastructure/services/core/cargo/cargo.service';
import { SexoResponse } from '../../../_infrastructure/dtos/core/sexo/SexoResponse';
import { SexoService } from '../../../_infrastructure/services/core/sexo/sexo.service';
import { RacaResponse } from '../../../_infrastructure/dtos/core/raca/RacaResponse';
import { RacaService } from '../../../_infrastructure/services/core/raca/raca.service';
import { NecessidadeEspecialService } from '../../../_infrastructure/services/core/necessidadeEspecial/necessidade-especial.service';
import { NecessidadeEspecialResponse } from '../../../_infrastructure/dtos/core/necessidadeEspecial/NecessidadeEspecialResponse';
import { UsuarioResponse } from '../../../_infrastructure/dtos/core/usuario/UsuarioResponse';
import { UsuarioService } from '../../../_infrastructure/services/core/usuario/usuario.service';
import { UpdateUsuarioLoggedDto } from '../../../_infrastructure/dtos/core/usuario/UpdateUsuarioLoggedDto';

@Component({
  selector: 'app-meu-perfil',
  standalone: false,
  templateUrl: './meu-perfil.component.html',
  styleUrl: './meu-perfil.component.css'
})
export class MeuPerfilComponent extends UnsubscribeOnDestroyAdapter implements OnInit {
  usuarioForm!: UntypedFormGroup;
  hide: boolean = true;
  previewImage: string | null = null;
  midiaBase64: MidiaImageBase64Dto | null = null;
  perfis: PerfilResponse[] = [];
  cargos: CargoResponse[] = [];
  sexos: SexoResponse[] = [];
  racas: RacaResponse[] = [];
  necessidadeEspecial: NecessidadeEspecialResponse[] = [];
  usuario!: UsuarioResponse;
  perfilNome!: string;
  defaultImage: string = '../../../../assets/transparencia-images/profile.png';
  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly authService: AuthService,
    private readonly usuarioService: UsuarioService,
    private readonly mediaImageService: MediaImageService,
    private readonly perfilService: PerfilService,
    private readonly formService: FormService,
    private readonly cargoService: CargoService,
    private readonly sexoService: SexoService,
    private readonly racaService: RacaService,
    private readonly necessidadeEspecialService: NecessidadeEspecialService,
    public dialogRef: MatDialogRef<MeuPerfilComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: DialogData<any>,
  ) {
    super();

  }

  get f() {
    return this.usuarioForm.controls;
  }

  ngOnInit() {
    this.createEmptyForm();
    this.subs.sink = this.authService.currentUser.subscribe(user => {
      if (user) {
        this.loadUsuario();
        this.perfilNome = user.perfilNome;
      }
    });
  }

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const file = input.files[0];

    try {
      const midia = await this.mediaImageService.processarImagem(file, {
        tamanhoMaxMb: 10
      });

      this.midiaBase64 = midia;
      this.previewImage = this.mediaImageService.gerarPreview(midia);
      this.usuarioForm.patchValue({ midia });

    } catch (error: any) {
      Swal.fire('Erro', error.message || 'Falha ao processar a imagem.', 'error');
    }
  }

  private createEmptyForm() {
    this.usuarioForm = this.formBuilder.group({
      nomeSocial: [null],
      telefone: [null],
      sexoId: [null],
      racaId: [null],
      nacionalidade: [null],
      naturalidade: [null],
      setor: [null],
      cargoId: [null],
      dataNascimento: [null, [Validators.required, this.formService.dateOfBirthValidator()]],
      midia: [null],
      necessidadeEspeciaisIds: [[]]
    });
  }

  private updateFormFromUsuario() {
    const dataNascimento = this.usuario.pessoa.dataNascimento
      ? this.usuario.pessoa.dataNascimento.substring(0, 10)
      : null;

    this.usuarioForm.patchValue({
      nomeSocial: this.usuario.pessoa.nomeSocial,
      telefone: this.usuario.pessoa.telefone,
      sexoId: this.usuario.pessoa.sexo?.id,
      racaId: this.usuario.pessoa.raca?.id,
      nacionalidade: this.usuario.pessoa.nacionalidade,
      naturalidade: this.usuario.pessoa.naturalidade,
      setor: this.usuario.pessoa.setor,
      cargoId: this.usuario.pessoa.cargo?.id,
      dataNascimento,
      necessidadeEspeciaisIds:
        this.usuario.pessoa.necessidadeEspecial?.map(x => x.id) || []
    });

    this.usuarioForm.enable();
  }


  private buildUpdateData(): UpdateUsuarioLoggedDto {
    const formValue = this.usuarioForm.value;

    return {
      nomeSocial: formValue.nomeSocial,
      dataNascimento: new Date(formValue.dataNascimento).toISOString(),
      telefone: formValue.telefone,
      sexoId: formValue.sexoId,
      racaId: formValue.racaId,
      nacionalidade: formValue.nacionalidade,
      naturalidade: formValue.naturalidade,
      setor: formValue.setor,
      cargoId: formValue.cargoId,
      midia: this.midiaBase64,
      necessidadeEspeciaisIds: formValue.necessidadeEspeciaisIds
    };
  }

  onSubmit() {
    if (this.usuarioForm.invalid) {
      this.usuarioForm.markAllAsTouched();
      return;
    }

    Swal.fire({
      title: 'Alterando Registro...',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading()
    });

    const updateItem: UpdateUsuarioLoggedDto = this.buildUpdateData();

    this.subs.sink = this.usuarioService.updateUsuarioLogged(updateItem)
      .subscribe({
        next: (result) => {
          this.dialogRef.close(result);
        },
        error: (error) => {
          console.error(error);
        },
        complete: () => {
          Swal.close();
        }
      });
  }

  loadPerfis() {
    this.perfilService.getAll()
      .subscribe({
        next: (result) => {
          this.perfis = result.data;
        },
        error: (error) => {
          console.error(error);
        },
        complete: () => {
          Swal.close();
        }
      });
  }

  loadCargos() {
    this.cargoService.getAll()
      .subscribe({
        next: (result) => {
          this.cargos = result.data;
        },
        error: (error) => {
          console.error(error);
        }
      });
  }

  loadSexos() {
    this.sexoService.getAll()
      .subscribe({
        next: (result) => {
          this.sexos = result.data;
        },
        error: (error) => {
          console.error(error);
        }
      });
  }

  loadRacas() {
    this.racaService.getAll()
      .subscribe({
        next: (result) => {
          this.racas = result.data;
        },
        error: (error) => {
          console.error(error);
        }
      });
  }

  loadNecessidadeEspecial() {
    this.necessidadeEspecialService.getAll()
      .subscribe({
        next: (result) => {
          this.necessidadeEspecial = result.data;
        },
        error: (error) => {
          console.error(error);
        }
      });
  }

  loadUsuario() {
    Swal.showLoading();
    this.usuarioForm.disable();
    this.usuarioService.getUserLogged()
      .subscribe({
        next: (result) => {
          this.usuario = result.data;
          this.loadPerfis();
          this.loadCargos();
          this.loadSexos();
          this.loadRacas();
          this.loadNecessidadeEspecial();
          this.updateFormFromUsuario();
          if (this.usuario.pessoa.midia) {
            this.previewImage = this.mediaImageService.gerarPreview(this.usuario.pessoa.midia);
          }
        },
        error: (error) => {
          console.error(error);
        },
        complete: () => {
          Swal.close();
        }
      });
  }
}